import { useState, useCallback, useEffect } from 'react';

interface UseBiometricAuthResult {
  isSupported: boolean;
  isEnabled: boolean;
  isAuthenticating: boolean;
  error: string | null;
  enableBiometric: (userId: string) => Promise<boolean>;
  disableBiometric: () => void;
  authenticateWithBiometric: () => Promise<boolean>;
  checkBiometricAvailability: () => Promise<boolean>;
}

const CREDENTIAL_ID_KEY = 'rdm_biometric_credential_id';
const BIOMETRIC_USER_KEY = 'rdm_biometric_user_id';

// Check if WebAuthn is supported
function isWebAuthnSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.PublicKeyCredential !== undefined &&
    typeof window.PublicKeyCredential === 'function'
  );
}

// Convert ArrayBuffer to Base64 string
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

// Convert Base64 string to ArrayBuffer
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export function useBiometricAuth(): UseBiometricAuthResult {
  const [isSupported, setIsSupported] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check support on mount
  useEffect(() => {
    const supported = isWebAuthnSupported();
    setIsSupported(supported);

    // Check if biometric is already enabled
    const credentialId = localStorage.getItem(CREDENTIAL_ID_KEY);
    setIsEnabled(!!credentialId);
  }, []);

  // Check if platform authenticator (Face ID, Touch ID, Windows Hello) is available
  const checkBiometricAvailability = useCallback(async (): Promise<boolean> => {
    if (!isWebAuthnSupported()) return false;

    try {
      const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      return available;
    } catch {
      return false;
    }
  }, []);

  // Enable biometric authentication for the user
  const enableBiometric = useCallback(async (userId: string): Promise<boolean> => {
    if (!isSupported) {
      setError('Biometric authentication is not supported on this device');
      return false;
    }

    setIsAuthenticating(true);
    setError(null);

    try {
      // Check if platform authenticator is available
      const available = await checkBiometricAvailability();
      if (!available) {
        setError('No biometric authenticator available on this device');
        return false;
      }

      // Create credential
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);

      const userIdBuffer = new TextEncoder().encode(userId);

      const credential = await navigator.credentials.create({
        publicKey: {
          challenge,
          rp: {
            name: 'RDM Connect',
            id: window.location.hostname,
          },
          user: {
            id: userIdBuffer,
            name: userId,
            displayName: 'RDM User',
          },
          pubKeyCredParams: [
            { type: 'public-key', alg: -7 },  // ES256
            { type: 'public-key', alg: -257 }, // RS256
          ],
          authenticatorSelection: {
            authenticatorAttachment: 'platform',
            userVerification: 'required',
            residentKey: 'preferred',
          },
          timeout: 60000,
          attestation: 'none',
        },
      });

      if (credential && credential instanceof PublicKeyCredential) {
        // Store credential ID
        const credentialId = arrayBufferToBase64(credential.rawId);
        localStorage.setItem(CREDENTIAL_ID_KEY, credentialId);
        localStorage.setItem(BIOMETRIC_USER_KEY, userId);
        setIsEnabled(true);
        return true;
      }

      return false;
    } catch (err) {
      console.error('Biometric enrollment failed:', err);
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setError('Biometric enrollment was cancelled or denied');
        } else if (err.name === 'NotSupportedError') {
          setError('Biometric authentication is not supported');
        } else {
          setError(err.message);
        }
      }
      return false;
    } finally {
      setIsAuthenticating(false);
    }
  }, [isSupported, checkBiometricAvailability]);

  // Disable biometric authentication
  const disableBiometric = useCallback(() => {
    localStorage.removeItem(CREDENTIAL_ID_KEY);
    localStorage.removeItem(BIOMETRIC_USER_KEY);
    setIsEnabled(false);
  }, []);

  // Authenticate using biometric
  const authenticateWithBiometric = useCallback(async (): Promise<boolean> => {
    const credentialId = localStorage.getItem(CREDENTIAL_ID_KEY);

    if (!isSupported || !credentialId) {
      setError('Biometric authentication is not enabled');
      return false;
    }

    setIsAuthenticating(true);
    setError(null);

    try {
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);

      const credential = await navigator.credentials.get({
        publicKey: {
          challenge,
          rpId: window.location.hostname,
          allowCredentials: [
            {
              type: 'public-key',
              id: base64ToArrayBuffer(credentialId),
              transports: ['internal'],
            },
          ],
          userVerification: 'required',
          timeout: 60000,
        },
      });

      if (credential && credential instanceof PublicKeyCredential) {
        // Authentication successful
        return true;
      }

      return false;
    } catch (err) {
      console.error('Biometric authentication failed:', err);
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setError('Biometric authentication was cancelled');
        } else if (err.name === 'SecurityError') {
          setError('Security error during authentication');
        } else {
          setError(err.message);
        }
      }
      return false;
    } finally {
      setIsAuthenticating(false);
    }
  }, [isSupported]);

  return {
    isSupported,
    isEnabled,
    isAuthenticating,
    error,
    enableBiometric,
    disableBiometric,
    authenticateWithBiometric,
    checkBiometricAvailability,
  };
}
